// src/pages/ProcessingResultsPage.jsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert, Button, ButtonGroup, Toast, ToastContainer } from 'react-bootstrap';
import { getJobResults, saveUserAdjustments, getUserAdjustments, downloadOriginalImage, reprocessJob } from '../api/photoProcessingApi';
import ImageStage from '../components/ProcessingResults/ImageStage';
import CropStage from '../components/ProcessingResults/CropStage';
import RotatedStage from '../components/ProcessingResults/RotatedStage';
import GridStage from '../components/ProcessingResults/GridStage';
import StatusBar from '../components/ProcessingResults/StatusBar';
import ToolsContainer from '../components/ProcessingResults/ToolsContainer';
import useImageWithAuth from '../hooks/useImageWithAuth';
import { withCacheBust } from '../utils/mediaUtils';
import ShareJobModal from '../components/ProcessingResults/ShareJobModal';
import ReprocessJobModal from '../components/ProcessingResults/ReprocessJobModal';
import { Share2 } from 'lucide-react';
import { calculateMeanColor, calculateMedianPerChannel, calculateTrueMedianColor,
          brightenColorHsl, darkenColorHsl, createColorManager } from '../utils/ColorUtils';
import './ProcessingResultsPage.css';


const ProcessingResultsPage = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeStage, setActiveStage] = useState(0);
  const [selectedCells, setSelectedCells] = useState([]);
  const [originalGridStructure, setOriginalGridStructure] = useState([]);
  const [originalGridDimensions, setOriginalGridDimensions] = useState([]);
  const [gridStructure, setGridStructure] = useState([]);
  const [gridDimensions, setGridDimensions] = useState([]);
  const [colorMode, setColorMode] = useState('mean');
  const [statusInfo, setStatusInfo] = useState('');
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [gridColors, setGridColors] = useState([]);
  const [displayGridColors, setDisplayGridColors] = useState([]);
  const [brightnessAdjustment, setBrightnessAdjustment] = useState(0);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cellSizeFraction, setCellSizeFraction] = useState(0.45);
  const [cellSizeAdjustment, setCellSizeAdjustment] = useState(0); // Changed from fraction to adjustment
  const [imageRotation, setImageRotation] = useState(0);
  const [showInfo, setShowInfo] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState({
    original: false,
    cropped: false,
    rotated: false
  });
  const [colorsCalculated, setColorsCalculated] = useState(false);

  const [showShareModal, setShowShareModal] = useState(false);
  const [imageRegenerating, setImageRegenerating] = useState(false);

  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  // Image refs
  const originalImageRef = useRef(null);
  const croppedImageRef = useRef(null);
  const rotatedImageRef = useRef(null);
  const regenerationPollTimeoutRef = useRef(null);
  const reprocessPollTimeoutRef = useRef(null);

  // Color manager ref for tracking adjustments
  const colorManagerRef = useRef(null);

  const lastAdditionalRotationRef = useRef(0);

  // Refs for tracking color calculation dependencies
  const lastCalculationParams = useRef({
    gridStructure: null,
    colorMode: null,
    rotatedImageLoaded: false
  });

   // Reset image loaded states when jobData changes
  useEffect(() => {
    if (jobData) {
      setImagesLoaded({
        original: false,
        cropped: false,
        rotated: false
      });
      setColorsCalculated(false);
    }
  }, [jobData?.original_image, jobData?.cropped_image, jobData?.rotated_image]);

  // Conditionally call hooks only when jobData is available
  const [originalImage, originalImageStatus, originalImageError] = useImageWithAuth(
    'photo_processing',
    jobData ? jobData.original_image : null
  );

  const [croppedImage, croppedImageStatus, croppedImageError] = useImageWithAuth(
    'photo_processing',
    jobData ? jobData.cropped_image : null
  );

  const [rotatedImage, rotatedImageStatus, rotatedImageError] = useImageWithAuth(
    'photo_processing',
    jobData ? withCacheBust(jobData.rotated_image, jobData.user_modifications_saved_at) : null
  );



  // Set image refs when images are loaded - FIXED VERSION
  useEffect(() => {
    if (originalImage && originalImageStatus === 'loaded') {
      originalImageRef.current = originalImage;
      setImagesLoaded(prev => ({ ...prev, original: true }));
      console.log("Original image loaded and ref set");
    }
  }, [originalImage, originalImageStatus, jobData]); // Add jobData image path as dependency

  useEffect(() => {
    if (croppedImage && croppedImageStatus === 'loaded') {
      croppedImageRef.current = croppedImage;
      setImagesLoaded(prev => ({ ...prev, cropped: true }));
      console.log("Cropped image loaded and ref set");
    }
  }, [croppedImage, croppedImageStatus, jobData]); // Add jobData image path as dependency

  useEffect(() => {
    if (rotatedImage && rotatedImageStatus === 'loaded') {
      rotatedImageRef.current = rotatedImage;
      setImagesLoaded(prev => ({ ...prev, rotated: true }));
      console.log("Rotated image loaded and ref set");
    }
  }, [rotatedImage, rotatedImageStatus, jobData]); // Add jobData image path as dependency

  // Debug useEffect to track status changes
  useEffect(() => {
    console.log("Image statuses:", {
      original: originalImageStatus,
      cropped: croppedImageStatus,
      rotated: rotatedImageStatus
    });
  }, [originalImageStatus, croppedImageStatus, rotatedImageStatus]);



  const loadJobData = async () => {
    try {
      setLoading(true);
      const response = await getJobResults(jobId);
      setJobData(response);

      // Initialize grid structure from job data
      if (response.full_results && response.full_results.grid_structure) {
        initializeGridStructure(response);
      }

      // Initialize grid colors from extracted_colors if available
      if (response.extracted_colors && response.extracted_colors.length > 0) {
        console.log("Initializing grid colors from extracted_colors");
        setGridColors(response.extracted_colors);
        setColorsCalculated(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadJobData();
  }, [jobId]);

  // Add error handling
  useEffect(() => {
    if (originalImageError) {
      console.error("Original image error:", originalImageError);
    }
    if (croppedImageError) {
      console.error("Cropped image error:", croppedImageError);
    }
    if (rotatedImageError) {
      console.error("Rotated image error:", rotatedImageError);
    }
  }, [originalImageError, croppedImageError, rotatedImageError]);

  // Add this debug useEffect
    useEffect(() => {
      console.log("Job data loaded:", jobData);
      console.log("Image paths:", {
        original: jobData?.original_image,
        cropped: jobData?.cropped_image,
        rotated: jobData?.rotated_image
      });
    }, [jobData]);

  const initializeGridStructure = (jobData) => {
    const gridData = jobData.full_results.grid_structure;
    const [rows, cols] = jobData.grid_dimensions || [0, 0];

    // Create unified grid structure (preserves original sizes)
    const structure = [];
    for (let row = 0; row < rows; row++) {
      structure[row] = [];
      for (let col = 0; col < cols; col++) {
        const cellData = gridData[row] && gridData[row][col] ? gridData[row][col] : null;
        structure[row][col] = {
          id: `cell_${row}_${col}`,
          row,
          col,
          bbox: cellData?.bbox || null,
          confidence: cellData?.confidence || 0,
          isComputed: cellData?.is_computed || false,
          color: null,
          selected: false
        };
      }
    }

    const fraction = jobData.cell_fraction_size;
    const newStructure = updateCellRectsFromFraction(structure, [rows, cols], fraction);

    setGridDimensions([rows, cols]);
    setOriginalGridDimensions([rows, cols]);
    setGridStructure(newStructure);
    setOriginalGridStructure(JSON.parse(JSON.stringify(newStructure))); // Deep copy

    // Initialize with current adjustment
    updateCellSizesFromAdjustment(newStructure, [rows, cols], cellSizeAdjustment);
  };

   const updateCellRectsFromFraction = (structure, dimensions, fraction) => {
    const newStructure = [];
    const [rows, cols] = dimensions;
    for (let row = 0; row < rows; row++){
       newStructure.push([]);
       for (let col = 0; col < cols; col++){
            // Calculate centered smaller rect based on fraction
            const originalWidth = structure[row][col].bbox.width;
            const originalHeight = structure[row][col].bbox.height;
            const newWidth = originalWidth * fraction;
            const newHeight = originalHeight * fraction;
            const offsetX = (originalWidth - newWidth) / 2;
            const offsetY = (originalHeight - newHeight) / 2;
            newStructure[row].push( {
                ...structure[row][col],
                bbox: {
                    ...structure[row][col].bbox,
                    x: structure[row][col].bbox.x + offsetX,
                    y: structure[row][col].bbox.y + offsetY,
                    width: newWidth,
                    height: newHeight,
                }
            });
       }
    }

    return newStructure;
  };


  const updateCellSizesFromAdjustment = (structure, dimensions, adjustment) => {
    const newStructure = [];
    const [rows, cols] = dimensions;

    for (let row = 0; row < rows; row++) {
      newStructure.push([]);
      for (let col = 0; col < cols; col++) {
        if (structure[row][col].bbox) {
          // Calculate size adjustment by adding/subtracting pixels
          const originalWidth = structure[row][col].bbox.width;
          const originalHeight = structure[row][col].bbox.height;

          const newWidth = Math.max(0.00001, originalWidth + adjustment);
          const newHeight = Math.max(0.00001, originalHeight + adjustment);
          const offsetX = (originalWidth - newWidth) / 2;
          const offsetY = (originalHeight - newHeight) / 2;

          newStructure[row].push({
            ...structure[row][col],
            bbox: {
              ...structure[row][col].bbox,
              x: structure[row][col].bbox.x + offsetX,
              y: structure[row][col].bbox.y + offsetY,
              width: newWidth,
              height: newHeight,
            }
          });
        } else {
          newStructure[row].push(structure[row][col]);
        }
      }
    }
    handleGridStructureChange(newStructure, dimensions);
  };

  // Rotation functions
  const rotateGridStructure = useCallback((degrees) => {
    if (!gridStructure.length || !rotatedImageRef.current) return;

    const imageWidth = 1.0; // Normalized coordinates
    const imageHeight = 1.0;
    let newStructure = [];
    let newDimensions = [...gridDimensions];

    if (degrees === 90 || degrees === -270) {
      // 90° clockwise
      const [rows, cols] = gridDimensions;
      newDimensions = [cols, rows];

      for (let newRow = 0; newRow < cols; newRow++) {
        newStructure[newRow] = [];
        for (let newCol = 0; newCol < rows; newCol++) {
          const oldRow = rows - 1 - newCol;
          const oldCol = newRow;
          const cell = gridStructure[oldRow][oldCol];

          if (cell.bbox) {
            newStructure[newRow][newCol] = {
              ...cell,
              id: `cell_${newRow}_${newCol}`,
              row: newRow,
              col: newCol,
              bbox: {
                x: imageHeight - cell.bbox.y - cell.bbox.height,
                y: cell.bbox.x,
                width: cell.bbox.height,
                height: cell.bbox.width
              }
            };
          } else {
            newStructure[newRow][newCol] = {
              ...cell,
              id: `cell_${newRow}_${newCol}`,
              row: newRow,
              col: newCol
            };
          }
        }
      }
    } else if (degrees === -90 || degrees === 270) {
      // 90° counterclockwise
      const [rows, cols] = gridDimensions;
      newDimensions = [cols, rows];

      for (let newRow = 0; newRow < cols; newRow++) {
        newStructure[newRow] = [];
        for (let newCol = 0; newCol < rows; newCol++) {
          const oldRow = newCol;
          const oldCol = cols - 1 - newRow;
          const cell = gridStructure[oldRow][oldCol];

          if (cell.bbox) {
            newStructure[newRow][newCol] = {
              ...cell,
              id: `cell_${newRow}_${newCol}`,
              row: newRow,
              col: newCol,
              bbox: {
                x: cell.bbox.y,
                y: imageWidth - cell.bbox.x - cell.bbox.width,
                width: cell.bbox.height,
                height: cell.bbox.width
              }
            };
          } else {
            newStructure[newRow][newCol] = {
              ...cell,
              id: `cell_${newRow}_${newCol}`,
              row: newRow,
              col: newCol
            };
          }
        }
      }
    } else if (degrees === 180 || degrees === -180) {
      // 180°
      const [rows, cols] = gridDimensions;

      for (let newRow = 0; newRow < rows; newRow++) {
        newStructure[newRow] = [];
        for (let newCol = 0; newCol < cols; newCol++) {
          const oldRow = rows - 1 - newRow;
          const oldCol = cols - 1 - newCol;
          const cell = gridStructure[oldRow][oldCol];

          if (cell.bbox) {
            newStructure[newRow][newCol] = {
              ...cell,
              id: `cell_${newRow}_${newCol}`,
              row: newRow,
              col: newCol,
              bbox: {
                x: imageWidth - cell.bbox.x - cell.bbox.width,
                y: imageHeight - cell.bbox.y - cell.bbox.height,
                width: cell.bbox.width,
                height: cell.bbox.height
              }
            };
          } else {
            newStructure[newRow][newCol] = {
              ...cell,
              id: `cell_${newRow}_${newCol}`,
              row: newRow,
              col: newCol
            };
          }
        }
      }
    }

    handleGridStructureChange(newStructure, newDimensions); // Force color recalculation
  }, [gridStructure, gridDimensions]);

  // Initialize color manager when gridColors are available
  useEffect(() => {
    if (gridColors.length > 0 && gridColors[0].length > 0) {
      colorManagerRef.current = createColorManager(gridColors);
      setDisplayGridColors(gridColors);
      setBrightnessAdjustment(0);
    }
  }, [gridColors]);

  // Function to brighten colors
  const handleBrighten = useCallback((factor = 0.15) => {
    if (!colorManagerRef.current) return;

    const newColors = colorManagerRef.current.brighten(factor);
    setDisplayGridColors(newColors);
    setBrightnessAdjustment(colorManagerRef.current.getCurrentAdjustment());
    setHasUnsavedChanges(true);

    console.log(`Brightened colors by ${(factor * 100).toFixed(0)}%`);
  }, []);

  // Function to darken colors
  const handleDarken = useCallback((factor = 0.15) => {
    if (!colorManagerRef.current) return;

    const newColors = colorManagerRef.current.darken(factor);
    setDisplayGridColors(newColors);
    setBrightnessAdjustment(colorManagerRef.current.getCurrentAdjustment());
    setHasUnsavedChanges(true);

    console.log(`Darkened colors by ${(factor * 100).toFixed(0)}%`);
  }, []);

  // Function to reset colors to original
  const handleResetColors = useCallback(() => {
    if (!colorManagerRef.current) return;

    const originalColors = colorManagerRef.current.reset();
    setDisplayGridColors(originalColors);
    setBrightnessAdjustment(0);
    setHasUnsavedChanges(true);

    console.log("Reset colors to original");
  }, []);

  // Function to automatically adjust brightness for dark images
  const autoAdjustBrightness = useCallback(() => {
    if (!colorManagerRef.current || brightnessAdjustment !== 0) return;

    const originalColors = colorManagerRef.current.getOriginalColors();
    let needsBrightnessBoost = false;
    let darkCellCount = 0;
    let totalBrightness = 0;
    let cellCount = 0;

    // Analyze brightness levels
    for (let row = 0; row < originalColors.length; row++) {
      for (let col = 0; col < originalColors[row].length; col++) {
        const cell = originalColors[row][col];
        if (cell && cell.r !== undefined) {
          const brightness = (cell.r + cell.g + cell.b) / 3;
          totalBrightness += brightness;
          cellCount++;

          if (brightness < 50) { // Threshold for "too dark"
            darkCellCount++;
          }
        }
      }
    }

    const averageBrightness = totalBrightness / cellCount;
    const darkPercentage = (darkCellCount / cellCount) * 100;

    // Apply automatic adjustment if needed
    if (averageBrightness < 80 || darkPercentage > 30) {
      let autoFactor = 0;

      if (averageBrightness < 50) {
        autoFactor = 0.3; // Very dark - big boost
      } else if (averageBrightness < 70) {
        autoFactor = 0.20; // Dark - medium boost
      } else {
        autoFactor = 0.10; // Slightly dark - small boost
      }

      handleBrighten(autoFactor);
      console.log(`Auto-adjusted brightness: ${(autoFactor * 100).toFixed(0)}% boost`);
    }
  }, [brightnessAdjustment, handleBrighten]);

  // Call auto-adjust when colors are first calculated
  useEffect(() => {
    if (colorsCalculated && gridColors.length > 0 && brightnessAdjustment === 0) {
      // Small delay to ensure everything is rendered
      const timer = setTimeout(() => {
        autoAdjustBrightness();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [colorsCalculated, gridColors]);


   // Color calculation effect - triggers when dependencies change
  useEffect(() => {
    const shouldCalculateColors = () => {
      // Don't calculate if we already have colors from jobData.extracted_colors
      //if (colorsCalculated && gridColors.length > 0) {
      //  console.log("already calculated");
      //	return;
      //}

      // Check if we have all required data
      if (!rotatedImageRef.current || !gridStructure.length) {
        return false;
      }

      // Check if parameters have changed
      const currentParams = {
        gridStructure: JSON.stringify(gridStructure),
        colorMode,
        rotatedImageRef: rotatedImageRef.current
      };

      const paramsChanged = (
        currentParams.gridStructure !== lastCalculationParams.current.gridStructure ||
        currentParams.colorMode !== lastCalculationParams.current.colorMode ||
        currentParams.rotatedImageRef !== lastCalculationParams.current.rotatedImageRef
      );

      console.log("Params changed" + paramsChanged);

      return paramsChanged;
    };

    if (shouldCalculateColors()) {
	    console.log("Calculating grid colors due to dependency changes");
      calculateGridColors();

      // Update last calculation params
      lastCalculationParams.current = {
        gridStructure: JSON.stringify(gridStructure),
        colorMode,
        rotatedImageRef: rotatedImageRef.current
      };
    }
  }, [rotatedImageRef.current, gridStructure, colorMode]);

  // Color calculation function moved from GridStage
  const calculateGridColors = useCallback(async () => {
    const image = rotatedImageRef.current;
    if (!image || gridStructure.length === 0) return;

    console.log("Starting color calculation...");
    setStatusInfo("Calculating colors...");

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    canvas.width = image.width;
    canvas.height = image.height;
    ctx.drawImage(image, 0, 0);

    const colors = [];
    const rows = gridStructure.length;
    const cols = gridStructure[0]?.length || 0;

    for (let row = 0; row < rows; row++) {
      colors[row] = [];
      for (let col = 0; col < cols; col++) {
        const cell = gridStructure[row][col];
        let colorData = {
          id: cell.id,
          row: cell.row,
          col: cell.col,
          color: 'rgb(255, 255, 255)',
          originalColor: cell.originalColor,
          r: 255,
          g: 255,
          b: 255,
          confidence: cell.confidence,
          isComputed: cell.isComputed
        };

        if (cell.bbox) {
          // Calculate color from image data using the bbox
          const rectX = Math.max(0, Math.min(Math.floor(cell.bbox.x * image.width), image.width - 1));
          const rectY = Math.max(0, Math.min(Math.floor(cell.bbox.y * image.height), image.height - 1));
          const rectWidth = Math.max(1, Math.min(Math.floor(cell.bbox.width * image.width), image.width - rectX));
          const rectHeight = Math.max(1, Math.min(Math.floor(cell.bbox.height * image.height), image.height - rectY));

          const [color, r, g, b] = extractColor(ctx, rectX, rectY, rectWidth, rectHeight, colorMode);

          colorData = {
            ...colorData,
            color,
            r,
            g,
            b
          };
        }

        colors[row][col] = colorData;
      }
    }

    setGridColors(colors);
    setColorsCalculated(true);
    setHasUnsavedChanges(true);

    setStatusInfo(`Grid: ${rows}x${cols}, Mode: ${colorMode}, Colors extracted: ${rows * cols}`);
    console.log("Color calculation completed");
  }, [rotatedImageRef.current, gridStructure, colorMode]);

  // Extract color function moved from GridStage
  const extractColor = useCallback((ctx, x, y, width, height, mode) => {
    try {
      // Ensure coordinates are within bounds
      const safeX = Math.max(0, Math.min(x, ctx.canvas.width - 1));
      const safeY = Math.max(0, Math.min(y, ctx.canvas.height - 1));
      const safeWidth = Math.max(1, Math.min(width, ctx.canvas.width - safeX));
      const safeHeight = Math.max(1, Math.min(height, ctx.canvas.height - safeY));

      const imageData = ctx.getImageData(safeX, safeY, safeWidth, safeHeight);
      const data = imageData.data;

      // Extract RGB channels
      const rCodes = [];
      const gCodes = [];
      const bCodes = [];

      for (let i = 0; i < data.length; i += 4) {
        rCodes.push(data[i]);
        gCodes.push(data[i + 1]);
        bCodes.push(data[i + 2]);
      }

      if (rCodes.length === 0) return ['rgb(255, 255, 255)', 255, 255, 255];

      let r, g, b;
      const colorParams = { rCodes, gCodes, bCodes };

      switch (mode) {
        case 'mean':
          [r, g, b] = calculateMeanColor(colorParams);
          break;
        case 'median':
          [r, g, b] = calculateMedianPerChannel(colorParams);
          break;
        case 'trueMedian':
          [r, g, b] = calculateTrueMedianColor(colorParams);
          break;
        default:
          [r, g, b] = calculateMeanColor(colorParams);
      }

      return [`rgb(${r}, ${g}, ${b})`, r, g, b];
    } catch (error) {
      console.error('Error extracting color:', error);
      return ['rgb(255, 255, 255)', 255, 255, 255];
    }
  }, []);


  // Load user adjustments when job data is loaded
  useEffect(() => {
    if (jobData) {
      loadUserAdjustments();
    }
  }, [jobData]);

  const loadUserAdjustments = async () => {
    try {
      const adjustments = await getUserAdjustments(jobId);
      if (adjustments.data.has_adjustments) {
        if (adjustments.data.adjusted_grid_structure.length > 0) {
          const adjustedGridData = adjustments.data.adjusted_grid_structure;
          const [rows, cols] = adjustments.data.adjusted_grid_dimensions || [0, 0];

          const structure = [];
          for (let row = 0; row < rows; row++) {
            structure[row] = [];
            for (let col = 0; col < cols; col++) {
              const cellData = adjustedGridData[row] && adjustedGridData[row][col] ? adjustedGridData[row][col] : null;
              structure[row][col] = {
                id: `cell_${row}_${col}`,
                row,
                col,
                bbox: cellData?.bbox || null,
                confidence: cellData?.confidence || 0,
                isComputed: cellData?.is_computed || false,
                color: null,
                selected: false
              };
            }
          }
          setGridStructure(structure);
          setGridDimensions([rows, cols]);
          console.log("Adjusted grid structure loaded");
        }
        if (adjustments.data.colors.length > 0) {
          setGridColors(adjustments.data.colors);
        }
        setColorMode(adjustments.data.color_mode);
        setImageRotation(adjustments.data.additional_rotation);
      }
    } catch (error) {
      console.error('Error loading user adjustments:', error);
    }
  };

  // Handle cell size adjustment change
  const handleCellSizeAdjustmentChange = useCallback((newAdjustment) => {
    setCellSizeAdjustment(newAdjustment);
    updateCellSizesFromAdjustment(gridStructure, gridDimensions, newAdjustment);
    setHasUnsavedChanges(true);
    setGridColors([]);
  }, [gridStructure, gridDimensions]);

  // Reset to original grid structure
  const handleResetToOriginal = useCallback(() => {
    if (originalGridStructure.length > 0) {
      handleGridStructureChange(JSON.parse(JSON.stringify(originalGridStructure)), [...originalGridDimensions]);
      setToastMessage('Reset to original grid structure');
      setToastVariant('info');
      setShowToast(true);
    }
  }, [originalGridStructure, originalGridDimensions]);

  // Memoized handlers to prevent unnecessary re-renders
  const handleCellSelect = useCallback((cellId, isSelected) => {
    if (isSelected) {
      setSelectedCells(prev => [...prev, cellId]);
    } else {
      setSelectedCells(prev => prev.filter(id => id !== cellId));
    }
  }, []);

  const handleCellUpdate = useCallback((cellId, newProps) => {
    setGridStructure(prev => {
      const newStructure = prev.map(row =>
        row.map(cell => {
          if (cell.id === cellId) {
            const updatedBbox = cell.bbox ? { ...cell.bbox } : { x: 0, y: 0, width: 0, height: 0 };

            if (newProps.x !== undefined) updatedBbox.x = newProps.x;
            if (newProps.y !== undefined) updatedBbox.y = newProps.y;
            if (newProps.width !== undefined) updatedBbox.width = newProps.width;
            if (newProps.height !== undefined) updatedBbox.height = newProps.height;

            return {
              ...cell,
              bbox: updatedBbox
            };
          }
          return cell;
        })
      );
      return newStructure;
    });
    setHasUnsavedChanges(true);
    setColorsCalculated(false);
  }, []);

  // Handle grid structure changes (from tools)
  const handleGridStructureChange = useCallback((newStructure, newDimensions) => {
    setGridStructure(newStructure);
    setGridDimensions(newDimensions);
    setColorsCalculated(false);
     // Force recalculation
    setHasUnsavedChanges(true);
  }, []);

  // Handle color mode change
  const handleColorModeChange = useCallback((newColorMode) => {
    if (newColorMode !== colorMode) {
      setColorMode(newColorMode);
      setColorsCalculated(false); // Force recalculation
      setHasUnsavedChanges(true);
    }
  }, [colorMode]);

  const handleStatusUpdate = useCallback((info) => {
    setStatusInfo(info);
  }, []);

  const handleImageLoaded = useCallback((imageType) => {
    setImagesLoaded(prev => ({ ...prev, [imageType]: true }));
  }, []);

  // After Save, rotated_image/rotated_thumbnail are regenerated on the backend
  // (job.image_regeneration_status: idle -> processing -> completed/failed).
  // Poll job results until that settles, then merge just the refreshed image
  // fields into jobData — NOT a full loadJobData(), which would reset
  // gridStructure/gridColors back to the raw (pre-edit) detections.
  const pollImageRegeneration = useCallback((targetJobId) => {
    if (regenerationPollTimeoutRef.current) {
      clearTimeout(regenerationPollTimeoutRef.current);
      regenerationPollTimeoutRef.current = null;
    }

    setImageRegenerating(true);
    let attempts = 0;
    const maxAttempts = 20; // ~30s at 1.5s interval

    const tick = async () => {
      attempts += 1;
      let regenStatus = null;
      try {
        const response = await getJobResults(targetJobId);
        regenStatus = response.image_regeneration_status;

        // Only merge the refreshed image fields in once regeneration has
        // actually finished — the cache-bust version in withCacheBust() is
        // derived from user_modifications_saved_at, so updating it earlier
        // (while the celery task may still be mid-write) would make
        // useImageWithAuth fetch once, immediately, and then never refetch
        // for the remainder of this poll (its effect only re-runs when the
        // path string itself changes).
        if (regenStatus === 'completed') {
          setJobData(prev => (prev ? {
            ...prev,
            rotated_image: response.rotated_image,
            rotated_thumbnail: response.rotated_thumbnail,
            user_modifications_saved_at: response.user_modifications_saved_at,
            image_regeneration_status: regenStatus,
          } : prev));
        }
      } catch (err) {
        console.error('Error polling image regeneration status:', err);
        setImageRegenerating(false);
        return;
      }

      if (regenStatus === 'completed' || regenStatus === 'failed' || attempts >= maxAttempts) {
        setImageRegenerating(false);
        return;
      }

      regenerationPollTimeoutRef.current = setTimeout(tick, 1500);
    };

    tick();
  }, []);

  useEffect(() => {
    return () => {
      if (regenerationPollTimeoutRef.current) {
        clearTimeout(regenerationPollTimeoutRef.current);
      }
      if (reprocessPollTimeoutRef.current) {
        clearTimeout(reprocessPollTimeoutRef.current);
      }
    };
  }, []);

  // After a reprocess request, the job goes back through the FULL pipeline
  // (Triton inference, then the crop -> rotate -> color-extraction chain —
  // see tasks.process_photo_analysis). Poll job.status until it settles,
  // then do a full loadJobData() — unlike pollImageRegeneration above,
  // everything derived from the previous run changes here (full_results,
  // grid_dimensions, image_type/orientation/crop_type, plate/crop bbox,
  // images), and the backend has already cleared the stale manual
  // adjustments (PhotoAnalysisJob.reset_for_reprocessing), so there is
  // nothing worth preserving from the pre-reprocess local state.
  //
  // Deliberately NOT wrapped in useCallback: it calls loadJobData(), which
  // is itself recreated every render, so memoizing this with an empty deps
  // array would pin it to the first render's closure (and, through
  // initializeGridStructure, to that render's stale cellSizeAdjustment).
  const pollReprocessing = (targetJobId) => {
    if (reprocessPollTimeoutRef.current) {
      clearTimeout(reprocessPollTimeoutRef.current);
      reprocessPollTimeoutRef.current = null;
    }

    setReprocessing(true);
    let attempts = 0;
    const maxAttempts = 60; // ~120s at 2s interval — a full inference + crop/rotate/color chain, not just an image regen

    const tick = async () => {
      attempts += 1;
      let response = null;
      try {
        response = await getJobResults(targetJobId);
      } catch (err) {
        console.error('Error polling reprocessing status:', err);
        setReprocessing(false);
        setToastMessage('Failed to check reprocessing status: ' + err.message);
        setToastVariant('danger');
        setShowToast(true);
        return;
      }

      const settled = response.status === 'completed' || response.status === 'failed';

      if (settled || attempts >= maxAttempts) {
        setReprocessing(false);

        if (response.status === 'completed') {
          setToastMessage('Reprocessing completed successfully');
          setToastVariant('success');
          setShowToast(true);
        } else if (response.status === 'failed') {
          setToastMessage('Reprocessing failed: ' + (response.error_message || 'Unknown error'));
          setToastVariant('danger');
          setShowToast(true);
        }

        // Local edit state referred to the pre-reprocess run — loadJobData()
        // repopulates gridStructure/gridColors/etc. from the fresh results.
        setSelectedCells([]);
        setHasUnsavedChanges(false);
        setColorsCalculated(false);
        setGridColors([]);
        setImageRotation(0);
        await loadJobData();
        return;
      }

      reprocessPollTimeoutRef.current = setTimeout(tick, 2000);
    };

    tick();
  };

  const handleConfirmReprocess = async (processingParams) => {
    try {
      setReprocessing(true);
      await reprocessJob(jobId, processingParams);
      setShowReprocessModal(false);
      setToastMessage('Job queued for reprocessing');
      setToastVariant('info');
      setShowToast(true);
      pollReprocessing(jobId);
    } catch (error) {
      setReprocessing(false);
      setToastMessage('Failed to start reprocessing: ' + error.message);
      setToastVariant('danger');
      setShowToast(true);
    }
  };

  const handleSaveAdjustments = async () => {
    try {
      setSaving(true);
      await saveUserAdjustments(jobId, gridStructure, gridDimensions, gridColors, colorMode, imageRotation);
      setHasUnsavedChanges(false);
      setToastMessage('Adjustments saved successfully!');
      setToastVariant('success');
      setShowToast(true);
      pollImageRegeneration(jobId);
    } catch (error) {
      setToastMessage(`Failed to save adjustments: ${error.message}`);
      setToastVariant('danger');
      setShowToast(true);
    } finally {
      setSaving(false);
    }
  };

  const rotateImage = useCallback((degrees) => {
      setImageRotation(prev => prev + degrees);
      setColorsCalculated(false);
      setHasUnsavedChanges(true);
    }, []);

      // Add this function to handle adding cells
        // Improved handleAddCells with proper spacing calculations
    const handleAddCells = useCallback((direction) => {
      if (!gridStructure.length) return;

      const [currentRows, currentCols] = gridDimensions;
      let newStructure = [];
      let newDimensions = [];

      // Calculate spacing and dimensions based on existing cells
      const existingCells = [];
      gridStructure.forEach(row => {
        row.forEach(cell => {
          if (cell.bbox) {
            existingCells.push(cell.bbox);
          }
        });
      });

      if (existingCells.length === 0) return;

      // Calculate average cell dimensions
      const avgWidth = existingCells.reduce((sum, bbox) => sum + bbox.width, 0) / existingCells.length;
      const avgHeight = existingCells.reduce((sum, bbox) => sum + bbox.height, 0) / existingCells.length;

      // Calculate spacing between cells
      let avgRowSpacing = 0;
      let avgColSpacing = 0;
      let spacingCount = 0;

      // Calculate row spacing
      for (let col = 0; col < currentCols; col++) {
        const colCells = [];
        for (let row = 0; row < currentRows; row++) {
          if (gridStructure[row][col].bbox) {
            colCells.push({
              row,
              y: gridStructure[row][col].bbox.y,
              height: gridStructure[row][col].bbox.height
            });
          }
        }
        colCells.sort((a, b) => a.y - b.y);

        for (let i = 1; i < colCells.length; i++) {
          const spacing = colCells[i].y - (colCells[i-1].y + colCells[i-1].height);
          if (spacing > 0) {
            avgRowSpacing += spacing;
            spacingCount++;
          }
        }
      }

      // Calculate column spacing
      for (let row = 0; row < currentRows; row++) {
        const rowCells = [];
        for (let col = 0; col < currentCols; col++) {
          if (gridStructure[row][col].bbox) {
            rowCells.push({
              col,
              x: gridStructure[row][col].bbox.x,
              width: gridStructure[row][col].bbox.width
            });
          }
        }
        rowCells.sort((a, b) => a.x - b.x);

        for (let i = 1; i < rowCells.length; i++) {
          const spacing = rowCells[i].x - (rowCells[i-1].x + rowCells[i-1].width);
          if (spacing > 0) {
            avgColSpacing += spacing;
            spacingCount++;
          }
        }
      }

      avgRowSpacing = spacingCount > 0 ? avgRowSpacing / (spacingCount / 2) : avgHeight * 0.1;
      avgColSpacing = spacingCount > 0 ? avgColSpacing / (spacingCount / 2) : avgWidth * 0.1;

      switch (direction) {
        case 'top':
          newDimensions = [currentRows + 1, currentCols];

          const topRow = [];
          for (let col = 0; col < currentCols; col++) {
            let topY = 1.0;
            let refX = col * (avgWidth + avgColSpacing);

            for (let row = 0; row < currentRows; row++) {
              if (gridStructure[row][col].bbox) {
                topY = Math.min(topY, gridStructure[row][col].bbox.y);
                refX = gridStructure[row][col].bbox.x;
                break;
              }
            }

            const newY = Math.max(0, topY - avgHeight - avgRowSpacing);

            topRow.push({
              id: `cell_0_${col}`,
              row: 0,
              col: col,
              bbox: {
                x: refX,
                y: newY,
                width: avgWidth,
                height: avgHeight
              },
              confidence: 0,
              isComputed: true,
              color: null,
              selected: false
            });
          }

          newStructure = [topRow];
          for (let row = 0; row < currentRows; row++) {
            const shiftedRow = gridStructure[row].map((cell, col) => ({
              ...cell,
              id: `cell_${row + 1}_${col}`,
              row: row + 1
            }));
            newStructure.push(shiftedRow);
          }
          break;

        case 'bottom':
          newDimensions = [currentRows + 1, currentCols];

          newStructure = gridStructure.map(row => [...row]);

          const bottomRow = [];
          for (let col = 0; col < currentCols; col++) {
            let bottomY = 0;
            let refX = col * (avgWidth + avgColSpacing);

            for (let row = currentRows - 1; row >= 0; row--) {
              if (gridStructure[row][col].bbox) {
                bottomY = Math.max(bottomY, gridStructure[row][col].bbox.y + gridStructure[row][col].bbox.height);
                refX = gridStructure[row][col].bbox.x;
                break;
              }
            }

            const newY = Math.min(1.0 - avgHeight, bottomY + avgRowSpacing);

            bottomRow.push({
              id: `cell_${currentRows}_${col}`,
              row: currentRows,
              col: col,
              bbox: {
                x: refX,
                y: newY,
                width: avgWidth,
                height: avgHeight
              },
              confidence: 0,
              isComputed: true,
              color: null,
              selected: false
            });
          }
          newStructure.push(bottomRow);
          break;

        case 'left':
          newDimensions = [currentRows, currentCols + 1];

          for (let row = 0; row < currentRows; row++) {
            let leftX = 1.0;
            let refY = row * (avgHeight + avgRowSpacing);

            for (let col = 0; col < currentCols; col++) {
              if (gridStructure[row][col].bbox) {
                leftX = Math.min(leftX, gridStructure[row][col].bbox.x);
                refY = gridStructure[row][col].bbox.y;
                break;
              }
            }

            const newX = Math.max(0, leftX - avgWidth - avgColSpacing);

            const newCell = {
              id: `cell_${row}_0`,
              row: row,
              col: 0,
              bbox: {
                x: newX,
                y: refY,
                width: avgWidth,
                height: avgHeight
              },
              confidence: 0,
              isComputed: true,
              color: null,
              selected: false
            };

            const shiftedRow = [newCell, ...gridStructure[row].map((cell, col) => ({
              ...cell,
              id: `cell_${row}_${col + 1}`,
              col: col + 1
            }))];

            newStructure.push(shiftedRow);
          }
          break;

        case 'right':
          newDimensions = [currentRows, currentCols + 1];

          for (let row = 0; row < currentRows; row++) {
            let rightX = 0;
            let refY = row * (avgHeight + avgRowSpacing);

            for (let col = currentCols - 1; col >= 0; col--) {
              if (gridStructure[row][col].bbox) {
                rightX = Math.max(rightX, gridStructure[row][col].bbox.x + gridStructure[row][col].bbox.width);
                refY = gridStructure[row][col].bbox.y;
                break;
              }
            }

            const newX = Math.min(1.0 - avgWidth, rightX + avgColSpacing);

            const newCell = {
              id: `cell_${row}_${currentCols}`,
              row: row,
              col: currentCols,
              bbox: {
                x: newX,
                y: refY,
                width: avgWidth,
                height: avgHeight
              },
              confidence: 0,
              isComputed: true,
              color: null,
              selected: false
            };

            const extendedRow = [...gridStructure[row], newCell];
            newStructure.push(extendedRow);
          }
          break;

        default:
          return;
      }

      handleGridStructureChange(newStructure, newDimensions);

      setToastMessage(`Added ${direction === 'top' || direction === 'bottom' ? 'row' : 'column'} at ${direction}`);
      setToastVariant('success');
      setShowToast(true);
    }, [gridStructure, gridDimensions]);

    // New handleDeleteCells function
    const handleDeleteCells = useCallback((direction) => {
      if (!gridStructure.length) return;

      const [currentRows, currentCols] = gridDimensions;
      let newStructure = [];
      let newDimensions = [];

      // Prevent deleting if only one row/column left
      if ((direction === 'top' || direction === 'bottom') && currentRows <= 1) {
        setToastMessage('Cannot delete - at least one row must remain');
        setToastVariant('warning');
        setShowToast(true);
        return;
      }

      if ((direction === 'left' || direction === 'right') && currentCols <= 1) {
        setToastMessage('Cannot delete - at least one column must remain');
        setToastVariant('warning');
        setShowToast(true);
        return;
      }

      switch (direction) {
        case 'top':
          newDimensions = [currentRows - 1, currentCols];
          // Remove first row and update IDs
          for (let row = 1; row < currentRows; row++) {
            const updatedRow = gridStructure[row].map((cell, col) => ({
              ...cell,
              id: `cell_${row - 1}_${col}`,
              row: row - 1
            }));
            newStructure.push(updatedRow);
          }
          break;

        case 'bottom':
          newDimensions = [currentRows - 1, currentCols];
          // Remove last row
          newStructure = gridStructure.slice(0, -1);
          break;

        case 'left':
          newDimensions = [currentRows, currentCols - 1];
          // Remove first column and update IDs
          for (let row = 0; row < currentRows; row++) {
            const updatedRow = gridStructure[row].slice(1).map((cell, col) => ({
              ...cell,
              id: `cell_${row}_${col}`,
              col: col
            }));
            newStructure.push(updatedRow);
          }
          break;

        case 'right':
          newDimensions = [currentRows, currentCols - 1];
          // Remove last column
          for (let row = 0; row < currentRows; row++) {
            newStructure.push(gridStructure[row].slice(0, -1));
          }
          break;

        default:
          return;
      }

      handleGridStructureChange(newStructure, newDimensions);

      setToastMessage(`Deleted ${direction === 'top' || direction === 'bottom' ? 'row' : 'column'} from ${direction}`);
      setToastVariant('success');
      setShowToast(true);
    }, [gridStructure, gridDimensions]);

  const handleShowInfo = useCallback(() => {
      setShowInfo(true);
    }, []);

  const handleCloseInfo = useCallback(() => {
      setShowInfo(false);
    }, []);

  const handleDownloadOriginal = useCallback(async () => {
      try {
        await downloadOriginalImage(jobId, jobData?.original_image_filename);
      } catch (error) {
        setToastMessage(`Failed to download original image: ${error.message}`);
        setToastVariant('danger');
        setShowToast(true);
      }
    }, [jobId, jobData]);


  // Memoized stages to prevent re-creation on every render
  const stages = useMemo(() => [
    {
      title: 'Original Image',
      iconClass: 'bi bi-image',
      component: (
        <ImageStage
          jobData={jobData}
          originalImageRef={originalImageRef}
          onStatusUpdate={handleStatusUpdate}
          onImageLoaded={() => handleImageLoaded('original')}
          isLoaded={imagesLoaded.original}
          externalShowInfo={showInfo}
          onInfoClose={() => handleCloseInfo()}
        />
      )
    },
    {
      title: 'Cropped Image',
      iconClass: 'bi bi-crop',
      component: (
        <CropStage
          jobData={jobData}
          originalImageRef={originalImageRef}
          croppedImageRef={croppedImageRef}
          onStatusUpdate={handleStatusUpdate}
          onImageLoaded={() => handleImageLoaded('cropped')}
          isLoaded={imagesLoaded.cropped}
          skipProcessing={!imagesLoaded.original}
        />
      )
    },
    {
      title: 'Rotated & Detected',
      iconClass: 'bi bi-bounding-box-circles',
      component: (
        <RotatedStage
          jobData={jobData}
          croppedImageRef={croppedImageRef}
          rotatedImageRef={rotatedImageRef}
          gridStructure={gridStructure}
          selectedCells={selectedCells}
          cellSizeAdjustment={cellSizeAdjustment}
          onCellSelect={handleCellSelect}
          onCellUpdate={handleCellUpdate}
          onCellSizeAdjustmentChange={handleCellSizeAdjustmentChange}
          onStatusUpdate={handleStatusUpdate}
          onImageLoaded={() => handleImageLoaded('rotated')}
          isLoaded={imagesLoaded.rotated}
          skipProcessing={!imagesLoaded.cropped}
          additionalRotation={imageRotation}
	  lastAdditionalRotationRef={lastAdditionalRotationRef}// Add this prop
        />
      )
    },
    {
      title: 'Grid Analysis',
      iconClass: 'bi bi-grid',
      component: (
        <GridStage
          jobData={jobData}
          rotatedImageRef={rotatedImageRef}
          gridStructure={gridStructure}
          selectedCells={selectedCells}
          colorMode={colorMode}
          displayGridColors={displayGridColors}
          colorsCalculated={colorsCalculated}
          onCellSelect={handleCellSelect}
          onStatusUpdate={handleStatusUpdate}
          skipProcessing={!imagesLoaded.rotated}
        />
      )
    }
  ], [
    jobData,
    selectedCells,
    colorMode,
    gridStructure,
    displayGridColors,
    gridColors,
    colorsCalculated,
    cellSizeAdjustment,
    imagesLoaded,
    handleImageLoaded,
    handleCellSelect,
    handleCellUpdate,
    handleCellSizeAdjustmentChange,
    handleStatusUpdate,
    imageRotation,
    showInfo,
    handleCloseInfo,
    handleBrighten,
    handleDarken,
    handleResetColors
  ]);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert variant="danger">
          <Alert.Heading>Error Loading Analysis Results</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!jobData) {
    return (
      <Container>
        <Alert variant="warning">No analysis data found.</Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="analysis-results-page">
      <div className="main-content">
        <Row>
          <Col>
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h2>{jobData.original_image_filename} - Job #{jobId}</h2>
              <div className="d-flex gap-2">
                <Button variant="outline-secondary" onClick={() => navigate('/jobs')}>
                  <i className="bi bi-arrow-left me-2"></i>
                  Back to Photos
                </Button>
                {hasUnsavedChanges && (
                    <span className="text-warning small align-self-center">
      <i className="bi bi-exclamation-triangle me-1"></i>
      Unsaved changes
    </span>
                )}
                {imageRegenerating && (
                    <span className="text-muted small align-self-center">
      <Spinner animation="border" size="sm" className="me-1"/>
      Updating image…
    </span>
                )}
                {reprocessing && (
                    <span className="text-muted small align-self-center">
      <Spinner animation="border" size="sm" className="me-1"/>
      Reprocessing…
    </span>
                )}
                <Button
                    variant="outline-success"
                    onClick={() => setShowShareModal(true)}
                    className="d-flex align-items-center gap-2"
                    title="Share this job with groups"
                >
                  <Share2 size={18}/>
                  Share
                </Button>
                <ButtonGroup>
                  <Button
                      variant="outline-secondary"
                      onClick={handleResetToOriginal}
                      disabled={!originalGridStructure.length || reprocessing}
                      title="Undo local grid/color edits made since the last save"
                  >
                    <i className="bi bi-arrow-counterclockwise me-2"></i>
                    Reset to Original
                  </Button>
                  <Button
                      variant="outline-warning"
                      onClick={() => setShowReprocessModal(true)}
                      disabled={reprocessing}
                      title="Re-run this photo through Triton, replacing its current detections"
                  >
                    <i className="bi bi-arrow-repeat me-2"></i>
                    Reprocess
                  </Button>
                </ButtonGroup>
                <Button
                    variant="primary"
                    onClick={handleSaveAdjustments}
                    disabled={saving || !hasUnsavedChanges || reprocessing}
                    className="save-button"
                >
                  {saving ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2"/>
                        Saving...
                      </>
                  ) : (
                      <>
                        <i className="bi bi-save me-2"></i>
                        Save Adjustments
                      </>
                  )}
                </Button>
              </div>
            </div>
          </Col>
        </Row>

        {jobData.status === 'failed' && (
          <Row className="mb-4">
            <Col>
              <Alert variant="danger" className="d-flex justify-content-between align-items-center">
                <div>
                  <strong>This job failed to process.</strong>{' '}
                  {jobData.error_message || 'No error details were recorded.'}
                </div>
                <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => setShowReprocessModal(true)}
                    disabled={reprocessing}
                    className="flex-shrink-0 ms-3"
                >
                  <i className="bi bi-arrow-repeat me-2"></i>
                  Reprocess
                </Button>
              </Alert>
            </Col>
          </Row>
        )}

        {/* Horizontal Stage Switcher */}
        <Row className="mb-4">
          <Col>
            <div className="stage-switcher-container">
              <div className="stage-switcher">
                {stages.map((stage, index) => (
                    <div
                        key={index}
                        className={`stage-tab ${activeStage === index ? 'active' : ''}`}
                        onClick={() => setActiveStage(index)}
                    >
                      <div className="stage-icon">
                        <i className={`${stage.iconClass}`}/>
                      </div>
                      <div className="stage-title">{stage.title}</div>
                    </div>
                ))}
              </div>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={2}>
            <ToolsContainer
                activeStage={activeStage}
                onShowInfo={() => handleShowInfo()}
                onDownloadOriginal={() => handleDownloadOriginal()}
                onRotateGrid={rotateGridStructure}
                onRotateImage={rotateImage}
              onColorModeChange={handleColorModeChange}
              colorMode={colorMode}
              selectedCells={selectedCells}
              onClearSelection={() => setSelectedCells([])}
              imagesLoaded={imagesLoaded}
              onCellSizeAdjustmentChange={handleCellSizeAdjustmentChange}
              onAddCells={handleAddCells}
              onDeleteCells={handleDeleteCells}
              onBrighten={handleBrighten}
              onDarken={handleDarken}
              onResetColors={handleResetColors}
              // Add this
            />
          </Col>
          <Col md={10}>
            <Card className="stage-card">
              <Card.Body className="stage-container p-0">
                {stages[activeStage].component}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <div className="status-bar-container">
        <Row>
          <Col>
            <StatusBar
              activeStage={activeStage}
              info={statusInfo}
              selectedCells={selectedCells}
              gridStructure={gridStructure}
              colors={gridColors}
              colorMode={colorMode}
            />
          </Col>
        </Row>
      </div>

      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={toastVariant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {toastVariant === 'success' ? 'Success' :
               toastVariant === 'danger' ? 'Error' :
               toastVariant === 'warning' ? 'Warning' : 'Info'}
            </strong>
          </Toast.Header>
          <Toast.Body className={toastVariant === 'danger' || toastVariant === 'success' ? 'text-white' : ''}>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>
      {jobData && (
        <ShareJobModal
          show={showShareModal}
          onHide={() => setShowShareModal(false)}
          job={jobData}
          onShareUpdated={() => {
            // Refresh job data to update any share-related UI
            loadJobData();
          }}
        />
      )}
      {jobData && (
        <ReprocessJobModal
          show={showReprocessModal}
          onHide={() => setShowReprocessModal(false)}
          mode="single"
          jobs={[jobData]}
          onConfirm={handleConfirmReprocess}
          submitting={reprocessing}
        />
      )}
    </Container>
  );
};

export default ProcessingResultsPage;
